/**
 * React Hook for WebSocket connection management
 * Handles automatic reconnection, authentication, and event handling
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { WS_URL, WS_EVENT_TYPES, WS_READY_STATES } from '../websocket';

const BASE_RECONNECT_DELAY = 1000; // first retry, roughly
const MAX_RECONNECT_DELAY = 30000; // ceiling for the backoff
const PING_INTERVAL = 30000; // 30 seconds

/**
 * Delay before retry number `attempt` (0-based): 1s, 2s, 4s… capped at 30s,
 * with jitter so a server restart doesn't bring every client back in lockstep.
 * There is no give-up: a long-lived tab must reconnect whenever the server
 * comes back, however long that takes.
 *
 * @param {number} attempt how many retries have already been made
 * @returns {number} milliseconds to wait
 */
export const reconnectDelay = (attempt) => {
    const ceiling = Math.min(BASE_RECONNECT_DELAY * 2 ** attempt, MAX_RECONNECT_DELAY);
    // Half fixed, half random: never retries faster than half the target delay.
    return Math.round(ceiling / 2 + Math.random() * (ceiling / 2));
};

export const useWebSocket = (options = {}) => {
    const {
        onCardUpdate,
        onDateUpdate,
        onExpeditionCompleted,
        onCompetitionRewards,
        onCompetitionPhaseChange,
        onTradeProposal,
        onNotification,
        onConnect,
        onDisconnect,
        onError,
        autoReconnect = true,
    } = options;

    const ws = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimeout = useRef(null);
    const pingInterval = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);

    /**
     * Get auth token for WebSocket connection
     */
    const getAuthToken = () => {
        return localStorage.getItem('authToken');
    };

    /**
     * Start ping interval to keep connection alive
     */
    const startPingInterval = useCallback(() => {
        if (pingInterval.current) {
            clearInterval(pingInterval.current);
        }

        pingInterval.current = setInterval(() => {
            if (ws.current && ws.current.readyState === WS_READY_STATES.OPEN) {
                ws.current.send(JSON.stringify({
                    type: WS_EVENT_TYPES.PING,
                    timestamp: Date.now(),
                }));
            }
        }, PING_INTERVAL);
    }, []);

    /**
     * Stop ping interval
     */
    const stopPingInterval = useCallback(() => {
        if (pingInterval.current) {
            clearInterval(pingInterval.current);
            pingInterval.current = null;
        }
    }, []);

    // Store handlers in refs to avoid reconnection on every render
    const handlersRef = useRef({
        onCardUpdate,
        onDateUpdate,
        onExpeditionCompleted,
        onCompetitionRewards,
        onCompetitionPhaseChange,
        onTradeProposal,
        onNotification,
        onConnect,
        onDisconnect,
        onError,
    });

    // Update handlers ref when they change
    useEffect(() => {
        handlersRef.current = {
            onCardUpdate,
            onDateUpdate,
            onExpeditionCompleted,
            onCompetitionRewards,
            onCompetitionPhaseChange,
            onTradeProposal,
            onNotification,
            onConnect,
            onDisconnect,
            onError,
        };
    }, [onCardUpdate, onDateUpdate, onExpeditionCompleted, onCompetitionRewards, onCompetitionPhaseChange, onTradeProposal, onNotification, onConnect, onDisconnect, onError]);

    /**
     * Handle incoming WebSocket messages
     */
    const handleMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.data);
            const { type } = data;


            switch (type) {
                case WS_EVENT_TYPES.CONNECTION_ESTABLISHED:
                    break;

                case WS_EVENT_TYPES.CARD_UPDATE:
                    handlersRef.current.onCardUpdate?.(data);
                    break;

                case WS_EVENT_TYPES.DATE_UPDATE:
                    handlersRef.current.onDateUpdate?.(data);
                    break;

                case WS_EVENT_TYPES.EXPEDITION_COMPLETED:
                    handlersRef.current.onExpeditionCompleted?.(data);
                    break;

                case WS_EVENT_TYPES.COMPETITION_REWARDS:
                    handlersRef.current.onCompetitionRewards?.(data);
                    break;

                case WS_EVENT_TYPES.COMPETITION_PHASE_CHANGE:
                    handlersRef.current.onCompetitionPhaseChange?.(data);
                    break;

                case WS_EVENT_TYPES.TRADE_PROPOSAL:
                    handlersRef.current.onTradeProposal?.(data);
                    break;

                case WS_EVENT_TYPES.NOTIFICATION:
                    handlersRef.current.onNotification?.(data);
                    break;

                case WS_EVENT_TYPES.PONG:
                    // Pong received, connection is alive
                    break;

                default:
                    console.warn('Unknown WebSocket message type:', type);
            }
        } catch (error) {
            console.error('Error parsing WebSocket message:', error);
        }
    }, []); // No dependencies - stable function

    /**
     * Connect to WebSocket
     */
    const connect = useCallback(() => {
        // Don't connect if not authenticated
        const token = getAuthToken();
        if (!token) {
            return;
        }

        // Don't create multiple connections
        if (ws.current && ws.current.readyState !== WS_READY_STATES.CLOSED) {
            return;
        }

        try {

            // Create WebSocket connection with auth token in URL
            const wsUrl = `${WS_URL}?token=${token}`;
            ws.current = new WebSocket(wsUrl);

            ws.current.onopen = () => {
                setIsConnected(true);
                setConnectionError(null);
                reconnectAttempts.current = 0;
                startPingInterval();
                handlersRef.current.onConnect?.();
            };

            ws.current.onmessage = handleMessage;

            ws.current.onerror = (error) => {
                console.error('WebSocket error:', error);
                setConnectionError('WebSocket connection error');
                handlersRef.current.onError?.(error);
            };

            ws.current.onclose = (event) => {
                setIsConnected(false);
                stopPingInterval();
                handlersRef.current.onDisconnect?.();

                // Keep retrying with exponential backoff. Giving up used to
                // leave a tab silently stale until a manual refresh.
                if (autoReconnect) {
                    const delay = reconnectDelay(reconnectAttempts.current);
                    reconnectAttempts.current += 1;
                    reconnectTimeout.current = setTimeout(() => {
                        connect();
                    }, delay);
                }
            };
        } catch (error) {
            console.error('Error creating WebSocket connection:', error);
            setConnectionError(error.message);
            handlersRef.current.onError?.(error);
        }
    }, [autoReconnect, handleMessage, startPingInterval, stopPingInterval]); // Removed handler dependencies

    /**
     * Disconnect from WebSocket
     */
    const disconnect = useCallback(() => {

        // Clear reconnect timeout
        if (reconnectTimeout.current) {
            clearTimeout(reconnectTimeout.current);
            reconnectTimeout.current = null;
        }

        // Stop ping interval
        stopPingInterval();

        // Close WebSocket
        if (ws.current) {
            ws.current.close();
            ws.current = null;
        }

        setIsConnected(false);
        reconnectAttempts.current = 0;
    }, [stopPingInterval]);

    /**
     * Retry right now instead of waiting out the backoff. Used for the events
     * that mean "the world just changed": the tab came back to the foreground,
     * or the machine came back online. A socket that died while the tab was
     * hidden would otherwise take up to 30s to notice.
     */
    const reconnectNow = useCallback(() => {
        if (!getAuthToken()) return;
        if (ws.current && ws.current.readyState === WS_READY_STATES.OPEN) return;

        if (reconnectTimeout.current) {
            clearTimeout(reconnectTimeout.current);
            reconnectTimeout.current = null;
        }
        // A deliberate retry starts the backoff over.
        reconnectAttempts.current = 0;
        connect();
    }, [connect]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') reconnectNow();
        };

        window.addEventListener('online', reconnectNow);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('online', reconnectNow);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [reconnectNow]);

    /**
     * Send a message through WebSocket
     */
    const send = useCallback((data) => {
        if (ws.current && ws.current.readyState === WS_READY_STATES.OPEN) {
            ws.current.send(JSON.stringify(data));
            return true;
        } else {
            console.warn('WebSocket is not connected');
            return false;
        }
    }, []);

    /**
     * Connect on mount, disconnect on unmount
     */
    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    return {
        isConnected,
        connectionError,
        send,
        reconnect: connect,
        disconnect,
    };
};

export default useWebSocket;
